import type { I18n, MessageDescriptor } from '@lingui/core';

import type { ApiErrorMessage } from './APIError';
import APIError, { apiErrorMessageFallback } from './APIError';

/**
 * Resolves a descriptor against the request's `i18n`. Not every store is configured with
 * one — `createStore`'s default extra argument is empty, and `Provider` reads an `i18n`
 * that is absent in trees with no `I18nProvider` — so fall back to the descriptor's own
 * text rather than throwing on `i18n._`. Never fall back to the `@lingui/core` global:
 * on the server that instance belongs to whichever request activated it last.
 */
const resolveDescriptor = (descriptor: MessageDescriptor, i18n: I18n | undefined): string =>
  i18n ? i18n._(descriptor) : apiErrorMessageFallback(descriptor);

/**
 * Resolves one of the mixed authored/server-supplied messages that `parseApiErrors`
 * produces. Strings came from the API and are passed through as-is.
 */
export const resolveApiErrorMessage = (message: ApiErrorMessage, i18n?: I18n): string =>
  typeof message === 'string' ? message : resolveDescriptor(message, i18n);

/**
 * Resolves the user-facing text of a thrown error. Anything carrying a `message` falls
 * back to it — not only real `Error`s, since some code paths reject with a bare object.
 */
export const resolveErrorMessage = (error: unknown, i18n?: I18n): string => {
  if (error instanceof APIError && error.descriptor) {
    return resolveDescriptor(error.descriptor, i18n);
  }

  const message = (error as { message?: unknown } | null)?.message;

  return typeof message === 'string' ? message : String(error);
};

export default resolveApiErrorMessage;
